import { useState, useRef, useCallback, useEffect } from 'react'

const MAX_RECORDING_DURATION_MS = 5 * 60 * 1000 // 5 minutes

export interface UseSpeechToTextReturn {
  /** Whether the mic is currently recording */
  isRecording: boolean
  /** Whether transcription is in progress (after recording stops) */
  isTranscribing: boolean
  /** Start recording from the microphone */
  startRecording: () => Promise<void>
  /** Stop recording and begin transcription */
  stopRecording: () => Promise<string | null>
  /** Toggle recording on/off — returns transcribed text when stopping, null when starting */
  toggleRecording: () => Promise<string | null>
  /** Last error message, if any */
  error: string | null
}

export function useSpeechToText(): UseSpeechToTextReturn {
  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const maxDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isRecordingRef = useRef(false)

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach(track => track.stop())
    streamRef.current = null
  }, [])

  const clearMaxDurationTimer = useCallback(() => {
    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current)
      maxDurationTimerRef.current = null
    }
  }, [])

  const stopRecording = useCallback(async (): Promise<string | null> => {
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current
      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        setIsRecording(false)
        isRecordingRef.current = false
        resolve(null)
        return
      }

      clearMaxDurationTimer()

      mediaRecorder.onstop = async () => {
        setIsRecording(false)
        isRecordingRef.current = false
        setIsTranscribing(true)

        try {
          const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType })
          chunksRef.current = []
          releaseStream()

          // Skip if recording was too short
          if (blob.size < 1000) {
            setIsTranscribing(false)
            setError(null)
            resolve(null)
            return
          }

          const arrayBuffer = await blob.arrayBuffer()
          const uint8 = new Uint8Array(arrayBuffer)
          let binary = ''
          for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i])
          const base64Audio = btoa(binary)
          const baseMimeType = mediaRecorder.mimeType.split(';')[0] // Strip codecs param
          const result = await window.electronAPI.transcribeAudio(base64Audio, baseMimeType)

          setIsTranscribing(false)

          if ('error' in result) {
            setError(result.error)
            resolve(null)
          } else {
            setError(null)
            resolve(result.text)
          }
        } catch (err) {
          setIsTranscribing(false)
          const message = err instanceof Error ? err.message : 'Transcription failed'
          setError(message)
          resolve(null)
        }
      }

      mediaRecorder.stop()
    })
  }, [clearMaxDurationTimer, releaseStream])

  const startRecording = useCallback(async () => {
    if (isRecordingRef.current) return

    try {
      setError(null)
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Microphone not available (requires HTTPS)')
        return
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      // Prefer webm/opus (small, OpenAI-supported), fall back to whatever is available
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4'

      const mediaRecorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      mediaRecorder.start(1000) // Collect data every second
      setIsRecording(true)
      isRecordingRef.current = true

      // Safety timeout: auto-stop after max duration
      maxDurationTimerRef.current = setTimeout(() => {
        if (isRecordingRef.current) {
          stopRecording()
        }
      }, MAX_RECORDING_DURATION_MS)
    } catch (err) {
      const message = err instanceof Error && err.name === 'NotAllowedError'
        ? 'Microphone access denied. Allow microphone access in System Settings.'
        : err instanceof Error ? err.message : 'Microphone access denied'
      setError(message)
    }
  }, [stopRecording])

  const toggleRecording = useCallback(async (): Promise<string | null> => {
    if (isRecordingRef.current) {
      return stopRecording()
    } else {
      await startRecording()
      return null
    }
  }, [startRecording, stopRecording])

  // Cleanup on unmount: stop recording and release mic
  useEffect(() => {
    return () => {
      clearMaxDurationTimer()
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }
      streamRef.current?.getTracks().forEach(track => track.stop())
    }
  }, [clearMaxDurationTimer])

  return {
    isRecording,
    isTranscribing,
    startRecording,
    stopRecording,
    toggleRecording,
    error,
  }
}
